import React, { Suspense, lazy } from 'react';
import ConfigurarSegundoFator from '../components/features/auth/ConfigurarSegundoFator';
import Anuncio from '../components/shared/Anuncio';
import AvisosDeFalha from '../components/shared/AvisoDeFalha';
import CentralDeNotificacoes from '../components/shared/Notificacao';
import { useAnunciarRota } from '../hooks/useAnunciarRota';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useUIStore } from '../store/uiStore';

import AuthScreen from '../components/features/auth';
import Casca from '../components/casca/Casca';
import CascaTopo from '../components/casca/CascaTopo';
import BarraSuperior from '../components/casca/BarraSuperior';
import BotaoRolar from '../components/casca/BotaoRolar';
import { usePreferenciaCasca } from '../components/casca/preferencias';
import PaginaNaoEncontrada from '../components/casca/PaginaNaoEncontrada';
import EstadoDeConexao from '../components/casca/EstadoDeConexao';
import SessaoExpirada from '../components/casca/SessaoExpirada';
import { Esqueleto } from '../components/egora';

// ==== LAZY LOADED MODULES ==========================
// External
const PropostaPublica = lazy(() => import('../components/features/propostas/PropostaPublica'));
const RedefinirSenha = lazy(() => import('../components/features/auth/RedefinirSenha'));
const AceitarConvite = lazy(() => import('../components/features/auth/AceitarConvite'));
const BoasVindas = lazy(() => import('../components/features/boasVindas'));
const RecuperarMFA = lazy(() => import('../components/features/auth/RecuperarMFA'));
const AdminMaster = lazy(() => import('../components/features/admin'));
const PortalCliente = lazy(() => import('../components/features/portal'));
const FormularioPublico = lazy(() => import('../components/features/formularios/FormularioPublico'));
const FormulariosModule = lazy(() => import('../components/features/formularios'));
// Página de exemplo do design system: só fora de produção (não entra no build final).
const ExemplosEgora = lazy(() => import('../components/egora/ExemplosEgora'));

// Features
const Home = lazy(() => import('../components/features/home'));
const Dashboard = lazy(() => import('../components/features/dashboard'));
const CRM = lazy(() => import('../components/features/crm'));
const Propostas = lazy(() => import('../components/features/propostas'));
const Financeiro = lazy(() => import('../components/features/financeiro'));
const Juridico = lazy(() => import('../components/features/juridico'));
const Documentos = lazy(() => import('../components/features/documentos'));
const RadarLegislativo = lazy(() => import('../components/features/radar'));
const Jurisprudencia = lazy(() => import('../components/features/jurisprudencia'));
const Configuracoes = lazy(() => import('../components/features/configuracoes'));
const Equipe = lazy(() => import('../components/features/colaboradores'));
const Agenda = lazy(() => import('../components/features/agenda'));
const Kanban = lazy(() => import('../components/features/kanban'));
const ConsultasExternas = lazy(() => import('../components/features/consultas'));
const Alertas = lazy(() => import('../components/features/alertas'));
const WhatsApp = lazy(() => import('../components/features/whatsapp'));
const WhatsAppConfiguracoes = lazy(() => import('../components/features/whatsapp/Configuracoes'));
const PainelDoAtendimento = lazy(() => import('../components/features/whatsapp/Painel'));


// Leftovers (Not refactored to features folder in Sprint 2)
const Assinaturas = lazy(() => import('../components/features/assinaturas'));
// ===================================================

// Carregando a tela: esqueleto, não spinner (DESIGN.md, "Estados obrigatórios").
const LoadingSpinner = () => (
  <div className="sj-pagina casca-carregando">
    <Esqueleto linhas={6} />
  </div>
);

// ROTA PROTEGIDA
const PrivateRoute = ({ children, moduloPermissao, somenteMaster = false }) => {
  const { usuarioLogado, temPermissao } = useAuth();
  if (!usuarioLogado) return <Navigate to="/login" replace />;
  // FRO-04: `somenteMaster` espelha no front o `check_master` que o backend já
  // exige. Sem isso a rota abria para qualquer logado e a tela só quebrava com
  // 403 no meio do carregamento — a trava existia, o aviso não.
  if (somenteMaster && !usuarioLogado.is_master) return <Navigate to="/" replace />;
  if (moduloPermissao && !temPermissao(moduloPermissao)) return <Navigate to="/" replace />;
  return children;
};

/**
 * ConfiguracoesRoute — lê a aba da URL e entrega ao módulo.
 *
 * Antes isto era `abaInicial={useUIStore().abaConfiguracao}`, uma chamada de
 * hook DENTRO do atributo do JSX. Funcionava por acidente (o JSX é avaliado no
 * corpo de MainLayout) e quebraria no dia em que esta <Route> virasse
 * condicional ou fosse gerada num `map`.
 *
 * O contrato do módulo (`abaInicial`) não mudou — só a fonte do valor.
 * Ver docs/arquitetura/ARQUITETURA-FRONTEND.md (R3).
 */
const ConfiguracoesRoute = ({ usuarioLogado }) => {
  const aba = useParams()['*'] || null;
  return <Configuracoes usuarioLogado={usuarioLogado} abaInicial={aba} />;
};

const MainLayout = () => {
  const { usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { processoParaAbrir, setProcessoParaAbrir } = useUIStore();
  // Preferências da casca (por usuário, neste aparelho): barra superior e densidade compacta.
  const [navegacao] = usePreferenciaCasca('navegacao');
  const [densidade] = usePreferenciaCasca('densidade');

  // A aba vai na URL, não no store global: quem chega em /configuracoes/etiquetas
  // — por link, por F5 ou pelo botão voltar — cai na aba certa.
  const irParaConfiguracao = (aba) => navigate(`/configuracoes/${aba}`);

  // A11Y-05: em SPA a página não recarrega, então o leitor de tela não percebe
  // que a tela mudou. Sem isto, a pessoa clica no menu e nada é anunciado.
  useAnunciarRota();

  return (
    <div className={`sj sj-app casca-raiz${densidade === 'compacta' ? ' casca-compacta' : ''}`}>
      {/* A11Y-04: região única de anúncio para leitor de tela. */}
      <Anuncio />
      {/* Avisa quando uma consulta falha, em vez de deixar o painel vazio
          parecendo que não há dado. */}
      <AvisosDeFalha />
      {/* Sem rede no aparelho: faixa fixa no topo. */}
      <EstadoDeConexao />
      <SessaoExpirada />
      {/* Toda resposta do sistema — sucesso, erro, confirmacao — no visual do
          sistema. Substitui as 361 caixas do navegador, que nenhum CSS alcanca. */}
      <CentralDeNotificacoes />
      {/* A11Y-12: primeiro alvo do Tab — pular a navegação e ir ao conteúdo. */}
      <a className="pular-para-conteudo" href="#conteudo-principal">Pular para o conteúdo</a>
      <Casca />
      <div className="sj-app__principal casca-principal">
        {/* Barra superior (opcional) e topo ficam presos no alto; só o conteúdo rola. */}
        <div className="sj-cabeca-fixa">
          {navegacao === 'superior' && <BarraSuperior />}
          <CascaTopo />
        </div>
        <div id="conteudo-principal" className="casca-rolagem">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
            <Route path="/dashboard" element={<PrivateRoute moduloPermissao="dashboard"><Dashboard /></PrivateRoute>} />
            <Route path="/crm" element={<PrivateRoute moduloPermissao="crm"><CRM /></PrivateRoute>} />
            <Route path="/propostas" element={<PrivateRoute moduloPermissao="crm"><Propostas irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="/financeiro" element={<PrivateRoute moduloPermissao="financeiro"><Financeiro irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="/juridico" element={<PrivateRoute moduloPermissao="juridico"><Juridico usuarioLogado={usuarioLogado} irParaConfig={irParaConfiguracao} processoInicial={processoParaAbrir} limparProcessoInicial={() => setProcessoParaAbrir(null)} /></PrivateRoute>} />
            <Route path="/agenda" element={<PrivateRoute><Agenda abrirNovaAgenda={() => {}} irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="agenda/*" element={<PrivateRoute><Agenda /></PrivateRoute>} />
            <Route path="kanban/*" element={<PrivateRoute><Kanban /></PrivateRoute>} />
            <Route path="notificacoes" element={<Navigate to="/alertas?aba=notificacoes" replace />} />
            <Route path="formularios/*" element={<PrivateRoute><FormulariosModule /></PrivateRoute>} />
            {/* Logs moraram aqui até 31/10/2026 e nunca abriam (o servidor só aceita o
                token do Bunker). Agora são a seção Logs do Bunker, em /admin. */}
            <Route path="logs" element={<Navigate to="/admin" replace />} />
            <Route path="projetos" element={<Navigate to="/kanban" replace />} />
            <Route path="/documentos" element={<PrivateRoute moduloPermissao="documentos"><Documentos irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="/radar" element={<PrivateRoute moduloPermissao="radar"><RadarLegislativo irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="/jurisprudencia" element={<PrivateRoute moduloPermissao="jurisprudencia"><Jurisprudencia irParaConfig={irParaConfiguracao} /></PrivateRoute>} />
            <Route path="/contratos" element={<PrivateRoute moduloPermissao="crm"><Assinaturas /></PrivateRoute>} />
            <Route path="/consultas" element={<PrivateRoute><ConsultasExternas /></PrivateRoute>} />
            {process.env.NODE_ENV !== 'production' && <Route path="/exemplos-egora" element={<ExemplosEgora />} />}
            <Route path="/alertas" element={<PrivateRoute><Alertas /></PrivateRoute>} />
            <Route path="/whatsapp" element={<PrivateRoute moduloPermissao="atendimento"><WhatsApp /></PrivateRoute>} />
            <Route path="/whatsapp/configuracoes" element={<PrivateRoute moduloPermissao="atendimento"><WhatsAppConfiguracoes /></PrivateRoute>} />
            <Route path="/whatsapp/painel" element={<PrivateRoute moduloPermissao="atendimento"><PainelDoAtendimento /></PrivateRoute>} />
            <Route path="/configuracoes/*" element={<PrivateRoute><ConfiguracoesRoute usuarioLogado={usuarioLogado} /></PrivateRoute>} />
            <Route path="/equipe" element={<PrivateRoute><Equipe /></PrivateRoute>} />
            
            <Route path="*" element={<PaginaNaoEncontrada />} />
          </Routes>
        </Suspense>
        </div>
        <BotaoRolar />
      </div>
    </div>
  );
};

export default function AppRoutes() {
  const { usuarioLogado } = useAuth();

  // Acesso restrito (2º fator zerado): nada além de configurar o novo fator.
  if (usuarioLogado?.mfa_setup) {
    return <ConfigurarSegundoFator />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* PUBLIC / STANDALONE */}
        {/* Item 3B: a página lê o token do fragmento (#t=), nunca da query. */}
        <Route path="/proposta-publica" element={<PropostaPublica />} />
        {/* Item 4C: link de definição de senha; o segredo vem no fragmento (#t=). */}
        <Route path="/redefinir-senha" element={<RedefinirSenha />} />
        <Route path="/convite" element={<AceitarConvite />} />
        {/* Item 4B-2: recuperação do 2º fator por convite; o token vem no fragmento (#t=). */}
        <Route path="/recuperar-mfa" element={<RecuperarMFA />} />
        <Route path="/f/:token" element={<FormularioPublico />} />
        <Route path="/admin" element={<AdminMaster />} />
        <Route path="/portal/*" element={<PortalCliente />} />
        
        {/* LAZY AUTH DESACOPLADA */}
        <Route path="/login" element={ usuarioLogado ? <Navigate to="/" replace /> : <AuthScreen onLoginSuccess={() => window.location.href = '/'} /> } />

        {/* Primeiro acesso do escritório (PrimeiroAcesso.dc.html): tela cheia, sem a casca. */}
        <Route path="/boas-vindas" element={ !usuarioLogado ? <Navigate to="/login" replace /> : <BoasVindas /> } />

        {/* PROTECTED MAIN APP */}
        <Route path="/*" element={ !usuarioLogado ? <Navigate to="/login" replace /> : <MainLayout /> } />
      </Routes>
    </Suspense>
  );
}
